"use client";

import { useLocale } from "@calcom/lib/hooks/useLocale";
import classNames from "@calcom/ui/classNames";
import { usePathname, useSearchParams } from "next/navigation";
import type { MutableRefObject } from "react";
import { useCallback, useEffect, useRef, useState } from "react";

const SHOW_DELAY_MS = 120;
const MIN_VISIBLE_MS = 250;
const MAX_VISIBLE_MS = 10000;

const clearTimer = (timerRef: MutableRefObject<ReturnType<typeof setTimeout> | null>): void => {
  if (!timerRef.current) return;

  clearTimeout(timerRef.current);
  timerRef.current = null;
};

const isModifiedClick = (event: MouseEvent): boolean =>
  event.altKey || event.ctrlKey || event.metaKey || event.shiftKey || event.button !== 0;

const isSameDocumentNavigation = (url: URL): boolean => {
  if (url.origin !== window.location.origin) return false;

  return (
    url.pathname === window.location.pathname &&
    url.search === window.location.search &&
    url.hash !== window.location.hash
  );
};

function shouldShowLoaderForAnchor(anchor: HTMLAnchorElement, event: MouseEvent): boolean {
  if (isModifiedClick(event)) return false;
  if (anchor.target && anchor.target !== "_self") return false;
  if (anchor.hasAttribute("download")) return false;

  const href = anchor.getAttribute("href");
  if (!href || href.startsWith("#") || href.startsWith("mailto:") || href.startsWith("tel:")) return false;

  const url = new URL(anchor.href);
  if (url.origin !== window.location.origin) return false;
  if (isSameDocumentNavigation(url)) return false;

  return `${url.pathname}${url.search}` !== `${window.location.pathname}${window.location.search}`;
}

function LoaderSpinner(): JSX.Element {
  return (
    <svg
      aria-hidden="true"
      className="h-4 w-4 animate-spin"
      fill="none"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path
        className="opacity-90"
        d="M4 12a8 8 0 0 1 8-8"
        stroke="currentColor"
        strokeLinecap="round"
        strokeWidth="4"
      />
    </svg>
  );
}

export function PageTransitionLoader(): JSX.Element {
  const { t } = useLocale();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const routeKey = `${pathname ?? ""}?${searchParams?.toString() ?? ""}`;
  const [isVisible, setIsVisible] = useState(false);
  const isVisibleRef = useRef(false);
  const visibleSinceRef = useRef<number | null>(null);
  const showTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const safetyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const hide = useCallback((): void => {
    clearTimer(showTimerRef);
    clearTimer(hideTimerRef);
    clearTimer(safetyTimerRef);
    visibleSinceRef.current = null;
    isVisibleRef.current = false;
    setIsVisible(false);
  }, []);

  const start = useCallback((): void => {
    clearTimer(hideTimerRef);
    clearTimer(safetyTimerRef);
    if (isVisibleRef.current || showTimerRef.current) return;

    showTimerRef.current = setTimeout(() => {
      showTimerRef.current = null;
      visibleSinceRef.current = performance.now();
      isVisibleRef.current = true;
      setIsVisible(true);
    }, SHOW_DELAY_MS);

    safetyTimerRef.current = setTimeout(hide, MAX_VISIBLE_MS);
  }, [hide]);

  const finish = useCallback((): void => {
    clearTimer(showTimerRef);

    const visibleSince = visibleSinceRef.current;
    if (!visibleSince) {
      hide();
      return;
    }

    const elapsed = performance.now() - visibleSince;
    const remaining = Math.max(MIN_VISIBLE_MS - elapsed, 0);
    clearTimer(hideTimerRef);
    hideTimerRef.current = setTimeout(hide, remaining);
  }, [hide]);

  useEffect(() => {
    const handleClick = (event: MouseEvent): void => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const anchor = target.closest("a[href]");
      if (anchor instanceof HTMLAnchorElement) {
        if (shouldShowLoaderForAnchor(anchor, event)) start();
        return;
      }

      const linkLikeElement = target.closest("[role='link']");
      if (linkLikeElement) start();
    };

    const handlePopState = (): void => start();

    document.addEventListener("click", handleClick, { capture: true });
    window.addEventListener("popstate", handlePopState);

    return (): void => {
      document.removeEventListener("click", handleClick, { capture: true });
      window.removeEventListener("popstate", handlePopState);
    };
  }, [start]);

  useEffect(() => {
    if (!routeKey) return;

    finish();
  }, [finish, routeKey]);

  useEffect(() => {
    return (): void => {
      clearTimer(showTimerRef);
      clearTimer(hideTimerRef);
      clearTimer(safetyTimerRef);
    };
  }, []);

  let visibilityClassName = "opacity-0";
  if (isVisible) {
    visibilityClassName = "opacity-100";
  }

  return (
    <div
      aria-hidden={!isVisible}
      aria-live="polite"
      className={classNames(
        "pointer-events-none fixed inset-x-0 top-0 z-[9999] transition-opacity duration-150",
        visibilityClassName
      )}>
      <div className="h-1 origin-left animate-[page-loader_1.3s_ease-in-out_infinite] bg-brand-default shadow-[0_0_18px_rgba(255,255,255,0.35)]" />
      <div className="fixed top-4 left-1/2 flex -translate-x-1/2 items-center gap-2 rounded-full border border-default bg-default px-3 py-2 text-emphasis text-sm shadow-lg">
        <LoaderSpinner />
        <span>{t("loading")}</span>
      </div>
      <style jsx global>{`
        @keyframes page-loader {
          0% {
            transform: scaleX(0);
            opacity: 0.65;
          }
          45% {
            transform: scaleX(0.68);
            opacity: 1;
          }
          100% {
            transform: scaleX(1);
            opacity: 0.45;
          }
        }
      `}</style>
    </div>
  );
}
